import type { ProjectSummary, SiteCrewSummary, SiteDaySummary, SiteDaySummaryText } from '@epc/contracts';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { Badge, EmptyState, ErrorBox, Spinner } from '../../ui/feedback';
import { Field, Input, Select } from '../../ui/form';
import { Page, PageHeader, Panel } from '../../ui/layout';
import { SITE_STATES } from './model';

type Search = { project?: string; day?: string; object?: string };

const todayStr = () => new Date().toLocaleDateString('en-CA');
function shift(date: string, n: number) {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
const dateLabel = (date: string) =>
  new Date(`${date}T00:00:00Z`).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    weekday: 'short',
    timeZone: 'UTC',
  });
const qty = (v: string) => (v.includes('.') ? v.replace(/\.?0+$/, '') : v);

/** Сводка дня по проекту (ПР-12): цифры из отчётов, замечания, отставания. */
export function DaySummaryPage() {
  const me = useMe();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as Search;
  const day = search.day ?? todayStr();
  useCrumbs([{ label: 'Площадка', to: '/site' }, { label: 'Сводка дня' }]);
  const projects = useQuery({
    queryKey: ['projects', me.tenant?.id, 'site-summary'],
    queryFn: () => api<ProjectSummary[]>('/projects'),
    enabled: Boolean(me.tenant?.staff),
  });
  const projectId = search.project ?? projects.data?.[0]?.id;
  const q = useQuery({
    queryKey: ['site', me.tenant?.id, 'day-summary', projectId, day, search.object],
    queryFn: () =>
      api<SiteDaySummary>('/site/day-summary', {
        query: {
          projectId: projectId!,
          date: day,
          ...(search.object ? { objectId: search.object } : {}),
        },
      }),
    enabled: Boolean(me.tenant?.staff && projectId),
  });
  const go = (patch: Search) =>
    navigate({
      to: '/site/summary',
      search: {
        project: projectId,
        day,
        object: search.object,
        ...patch,
      } as never,
    });

  if (!me.tenant?.staff)
    return (
      <Page>
        <ErrorBox error={new Error('Площадка доступна сотрудникам компании')} />
      </Page>
    );

  return (
    <Page>
      <PageHeader
        title="Сводка дня"
        subtitle={
          q.data ? `${q.data.projectCode} · ${dateLabel(q.data.date)}` : 'Отчёты, люди, техника, замечания'
        }
        actions={
          <>
            <Button
              aria-label="Назад"
              icon={<ChevronLeft className="size-4" />}
              onClick={() => go({ day: shift(day, -1) })}
            >
              <span className="md:hidden">Назад</span>
            </Button>
            <Button onClick={() => go({ day: todayStr() })}>Сегодня</Button>
            <Button
              aria-label="Вперёд"
              icon={<ChevronRight className="size-4" />}
              onClick={() => go({ day: shift(day, 1) })}
            >
              <span className="md:hidden">Вперёд</span>
            </Button>
          </>
        }
      />
      <div className="mb-4 max-w-md">
        <Field label="Проект">
          <Select
            value={projectId ?? ''}
            onChange={(e) => go({ project: e.target.value || undefined })}
            disabled={!projects.data?.length}
          >
            {(projects.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.code} · {p.name}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      {q.isPending ? <Spinner /> : null}
      <ErrorBox error={q.error ?? projects.error} />
      {projectId ? <CrewExportPanel projectId={projectId} day={day} /> : null}
      {q.data ? <SummaryBody data={q.data} /> : null}
    </Page>
  );
}

/** Выгрузка численности и техники по подрядчикам за период (ПР-19). */
function CrewExportPanel({ projectId, day }: { projectId: string; day: string }) {
  const [from, setFrom] = useState(() => {
    const d = new Date(`${day}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() - 6);
    return d.toISOString().slice(0, 10);
  });
  const [to, setTo] = useState(day);
  const qs = `from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
  const preview = useQuery({
    queryKey: ['site', 'crew-summary', projectId, from, to],
    queryFn: () => api<SiteCrewSummary>(`/site/projects/${projectId}/crew-summary`, { query: { from, to } }),
    enabled: Boolean(projectId && from && to && from <= to),
  });
  return (
    <Panel
      title="Численность и техника по подрядчикам"
      meta="за период · из отчётов смены"
      className="mb-5"
      actions={
        from <= to ? (
          <>
            <a
              className="inline-flex min-h-11 items-center rounded-sm px-3 text-[14px] text-signal underline-offset-2 hover:underline"
              href={`/api/v1/site/projects/${projectId}/crew-summary.xlsx?${qs}`}
            >
              Excel
            </a>
            <a
              className="inline-flex min-h-11 items-center rounded-sm px-3 text-[14px] text-signal underline-offset-2 hover:underline"
              href={`/api/v1/site/projects/${projectId}/crew-summary.pdf?${qs}`}
            >
              PDF
            </a>
          </>
        ) : null
      }
    >
      <div className="mb-3 grid gap-3 sm:grid-cols-2">
        <Field label="С">
          <Input type="date" className="!h-11" value={from} onChange={(e) => setFrom(e.target.value)} />
        </Field>
        <Field label="По">
          <Input type="date" className="!h-11" value={to} onChange={(e) => setTo(e.target.value)} />
        </Field>
      </div>
      {from > to ? (
        <p className="text-sm text-danger">Начало периода позже конца</p>
      ) : preview.isPending ? (
        <Spinner />
      ) : preview.error ? (
        <ErrorBox error={preview.error} />
      ) : preview.data?.contractors.length === 0 ? (
        <EmptyState title="Нет данных за период">
          Когда за период появятся отправленные или принятые отчёты с людьми и техникой, здесь будет сводка.
        </EmptyState>
      ) : (
        <ul className="flex flex-col gap-3">
          {preview.data?.contractors.map((c) => (
            <li key={c.contractorId ?? c.contractorName} className="text-[14px] text-ink">
              <div className="font-medium">{c.contractorName}</div>
              <div className="text-ink-2">
                людей {c.labor.length} · техники {c.machines.length}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function SummaryBody({ data }: { data: SiteDaySummary }) {
  return (
    <div className="flex flex-col gap-5">
      <SummaryText key={`${data.projectId}:${data.date}:${data.objectId ?? ''}`} data={data} />

      <Panel title={`Отчёты смены · ${data.reports.length}`}>
        {data.reports.length === 0 ? (
          <EmptyState title="Отчётов за день нет">
            Когда прорабы сдадут отчёты, здесь появятся состояние, фото и ссылки.
          </EmptyState>
        ) : (
          <ul className="divide-y divide-line">
            {data.reports.map((r) => (
              <li
                key={r.id}
                className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0">
                  <div className="text-[15px] font-medium text-ink">{r.packageName}</div>
                  <div className="text-[13px] text-ink-2">
                    {r.authorName} · работ {r.lineCount} · фото {r.photoCount}
                    {r.videoCount ? ` · видео ${r.videoCount}` : ''}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge
                    tone={r.state === 'accepted' ? 'success' : r.state === 'returned' ? 'warning' : 'neutral'}
                  >
                    {SITE_STATES[r.state as keyof typeof SITE_STATES] ?? r.state}
                  </Badge>
                  <Link
                    to="/site/review"
                    search={{ project: data.projectId, report: r.id } as never}
                    className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-sm px-3 text-[14px] text-signal underline-offset-2 hover:underline"
                  >
                    Открыть
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      {data.missing.length ? (
        <Panel title={`Не сдали отчёт · ${data.missing.length}`}>
          <ul className="flex flex-col gap-2">
            {data.missing.map((m) => (
              <li key={m.packageId} className="text-[14px] text-ink">
                {m.packageName}
                {m.foremanName ? <span className="text-ink-2"> · {m.foremanName}</span> : null}
              </li>
            ))}
          </ul>
          <div className="mt-3">
            <Link
              to="/site/missing"
              search={{ day: data.date, project: data.projectId } as never}
              className="inline-flex min-h-11 items-center text-[14px] text-signal underline-offset-2 hover:underline"
            >
              Полный список
            </Link>
          </div>
        </Panel>
      ) : null}

      <div className="grid gap-5 lg:grid-cols-2">
        <Panel title="Люди">
          {data.labor.length === 0 ? (
            <p className="text-[14px] text-ink-2">В отчётах за день люди не указаны.</p>
          ) : (
            <ul className="divide-y divide-line">
              {data.labor.map((l) => (
                <li key={l.profession} className="flex justify-between gap-3 py-2 text-[14px]">
                  <span className="text-ink">{l.profession}</span>
                  <span className="text-ink-2">
                    {l.headcount} чел. · {qty(l.hours)} ч
                    {l.idleHours !== '0' ? ` (простой ${qty(l.idleHours)})` : ''}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
        <Panel title="Техника">
          {data.machines.length === 0 ? (
            <p className="text-[14px] text-ink-2">В отчётах за день техника не указана.</p>
          ) : (
            <ul className="divide-y divide-line">
              {data.machines.map((m) => (
                <li key={m.machineId} className="flex justify-between gap-3 py-2 text-[14px]">
                  <span className="text-ink">{m.name}</span>
                  <span className="text-ink-2">
                    {qty(m.hours)} маш.-ч
                    {m.idleHours !== '0' ? ` (простой ${qty(m.idleHours)})` : ''}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      <Panel title={`Фото · ${data.photoCount}${data.videoCount ? ` · видео · ${data.videoCount}` : ''}`}>
        <p className="text-[14px] text-ink-2">
          {data.photoCount || data.videoCount
            ? 'Снимки и ролики открываются в карточке отчёта на проверке.'
            : 'К отчётам за день фото и видео не прикладывали.'}
        </p>
      </Panel>

      {data.remarks ? (
        <Panel title={`Открытые замечания · ${data.remarks.length}`}>
          {data.remarks.length === 0 ? (
            <p className="text-[14px] text-ink-2">Открытых замечаний по проекту нет.</p>
          ) : (
            <ul className="divide-y divide-line">
              {data.remarks.map((r) => (
                <li
                  key={r.id}
                  className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0">
                    <div className="text-[15px] font-medium text-ink">
                      {r.number}: {r.title}
                    </div>
                    <div className="text-[13px] text-ink-2">
                      Срок {r.dueDate.split('-').reverse().join('.')}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {r.stopWork ? <Badge tone="danger">Остановка</Badge> : null}
                    {r.overdue ? <Badge tone="warning">Просрочено</Badge> : null}
                    <Link
                      to="/supervision/remarks/$remarkId"
                      params={{ remarkId: r.id } as never}
                      className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-sm px-3 text-[14px] text-signal underline-offset-2 hover:underline"
                    >
                      Открыть
                    </Link>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      ) : null}

      {data.inspections ? (
        <Panel title={`Освидетельствование скрытых работ · ${data.inspections.length}`}>
          {data.inspections.length === 0 ? (
            <p className="text-[14px] text-ink-2">Вызовов на освидетельствование на эти дни нет.</p>
          ) : (
            <ul className="divide-y divide-line">
              {data.inspections.map((i) => (
                <li
                  key={i.id}
                  className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0">
                    <div className="text-[15px] font-medium text-ink [overflow-wrap:anywhere]">
                      {i.number}: {i.workName}
                    </div>
                    <div className="text-[13px] text-ink-2">
                      {new Date(i.scheduledAt).toLocaleString('ru-RU', {
                        dateStyle: 'short',
                        timeStyle: 'short',
                      })}
                      {i.nextActivityName ? ` · держит «${i.nextActivityName}»` : ''}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {i.overdue ? <Badge tone="warning">Не закрыто в срок</Badge> : null}
                    <Link
                      to="/supervision/inspections/$inspectionId"
                      params={{ inspectionId: i.id }}
                      className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-sm px-3 text-[14px] text-signal underline-offset-2 hover:underline"
                    >
                      Открыть
                    </Link>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      ) : null}

      <Panel title={`Отставания по графику · ${data.delays.length}`}>
        {data.delays.length === 0 ? (
          <p className="text-[14px] text-ink-2">
            Нет работ с плановым окончанием не позже выбранной даты без факта закрытия.
          </p>
        ) : (
          <ul className="divide-y divide-line">
            {data.delays.map((d) => (
              <li
                key={d.activityId}
                className="flex flex-wrap items-baseline justify-between gap-2 py-2 text-[14px]"
              >
                <span className="text-ink">
                  {d.code} · {d.name}
                </span>
                <span className="text-ink-2">
                  план {d.plannedFinish.split('-').reverse().join('.')} · {d.percent}%
                  {d.critical ? ' · критическая' : ''}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}

/** Текст сводки: шаблон по цифрам; по кнопке — пересказ Pilot, а если Pilot недоступен — шаблон и причина (ПР-12). */
function SummaryText({ data }: { data: SiteDaySummary }) {
  const pilot = useMutation({
    mutationFn: () =>
      api<SiteDaySummaryText>('/site/day-summary/pilot', {
        method: 'POST',
        body: {
          projectId: data.projectId,
          date: data.date,
          ...(data.objectId ? { objectId: data.objectId } : {}),
        },
      }),
  });
  const shown = pilot.data ?? { text: data.text, textSource: data.textSource, pilotNote: null };
  return (
    <Panel
      title="Текст сводки"
      meta={shown.textSource === 'pilot' ? 'Подготовлено ИИ (Pilot)' : 'по цифрам отчётов'}
      actions={
        shown.textSource === 'pilot' ? null : (
          <Button variant="secondary" size="lg" onClick={() => pilot.mutate()} disabled={pilot.isPending}>
            {pilot.isPending ? 'Pilot пишет…' : 'Текст Pilot'}
          </Button>
        )
      }
    >
      <p className="text-[15px] leading-6 text-ink">{shown.text}</p>
      {shown.pilotNote ? (
        <p className="mt-2 text-[13px] text-ink-2">
          Pilot недоступен: {shown.pilotNote.replace(/\.$/, '')}. Показан текст по цифрам.
        </p>
      ) : null}
      <ErrorBox error={pilot.error} />
    </Panel>
  );
}
